import React, { useState } from 'react';
import { Crown, Shield, Code, ClipboardList, Search, Loader2, Wand, PersonStanding } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const ROLES = [
  {
    key: 'CIO',
    label: 'Chief Information Officer',
    icon: Crown,
    description: 'Strategic overview with business impact analysis and executive-level risk assessment.'
  },
  {
    key: 'ISSO',
    label: 'ISSO / ATO Preparation',
    icon: Shield,
    description: 'Detailed security controls mapping and authorization documentation guidance.'
  },
  {
    key: 'Developer',
    label: 'Developer',
    icon: Code,
    description: 'Technical remediation steps with code examples and implementation priorities.'
  },
  {
    key: 'ProductOwner',
    label: 'Product Owner',
    icon: ClipboardList,
    description: 'User impact analysis and feature prioritization based on compliance requirements.'
  },
  {
    key: 'Auditor',
    label: 'Auditor',
    icon: Search,
    description: 'Evidence mapping and documentation for audit trails and compliance verification.'
  },
];



const InterpreterTab = ({ scanId }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [error, setError] = useState(null);
  const [cached, setCached] = useState(false);

  const handleRoleSelect = async (role) => {
    setSelectedRole(role);
    setAiResponse(null);
    setError(null);
    setLoading(true);
    // Timeout logic
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
        const cachedResponse = await fetch(`${ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Interpreter")}?interpretationRole=${role.key}`, { method: 'POST', signal: controller.signal, credentials: 'include' });
        clearTimeout(timeoutId);
        if (cachedResponse.status == 200) {
            setCached(true);
            const json = await cachedResponse.json();
            const jsonFormattedResponse = JSON.parse(json.response);
            setAiResponse({
                title: `Executive Summary for ${role.label}`,
                output: jsonFormattedResponse.output,
                tokensUsed: json.tokens_used,
                costUsd: json.cost_usd,
                lastUpdated: json.lastUpdated,
            });
        } else {
            await handleGenerate(role);
        }
    } catch (err) {
        setError('Unable to retrieve AI analysis. Please try again.');
    } finally {
        setLoading(false);
    }
  };

  const handleGenerate = async (role) => {
    try {
        setCached(false);
        setAiResponse(null);
        setError(null);
        setLoading(true);
        // Timeout logic
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);
        const response = await fetch(ApiNames.AI_HUB_INTERPRETER(scanId, role.key), { method: 'POST', signal: controller.signal, credentials: 'include' });
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error('Request failed');
        const json = await response.json();
        const jsonFormattedResponse = JSON.parse(json.response);
        setAiResponse({
            title: `Executive Summary for ${role.label}`,
            output: jsonFormattedResponse.output,
            tokensUsed: json.tokens_used,
            costUsd: json.cost_usd,
            lastUpdated: json.lastUpdated,
        });
    } catch (err) {
        setError("Unable to generate AI analysis. Please try again.");
    } finally {
        setLoading(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-8">
      <div className="w-full max-w-6xl">
            <div className="card-header flex items-center justify-between">
                <div className="card-title flex items-center gap-2">
                <PersonStanding className="w-6 h-6 text-blue-400" />
                <h3 className="font-bold text-lg">AI Interpreter - Explain Compliance Your Way</h3>
                </div>
                <span className="card-badge flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold"><Wand className="w-4 h-4 mr-1" />AI-Powered</span>
            </div>
            <p className="text-gray-400 mb-6 text-sm mt-2">
                Select your role to receive tailored compliance insights. Our AI translates complex technical findings into actionable intelligence for your specific needs.
            </p>
            <div className="persona-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {ROLES.map((role) => {
                const Icon = role.icon;
                return (
                    <button
                    key={role.key}
                    className={`persona-card flex flex-col items-center p-6 rounded-xl border-2 transition-all duration-200 shadow-sm cursor-pointer focus:outline-none h-full ${selectedRole?.key === role.key ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-105' : 'border-base-300 bg-base-100 hover:border-blue-400'}`}
                    onClick={() => handleRoleSelect(role)}
                    disabled={loading}
                    type="button"
                    >
                    <span className="persona-icon mb-2">
                        <Icon className="w-8 h-8 text-blue-400" />
                    </span>
                    <h4 className="font-semibold text-base mb-1 text-center">{role.label}</h4>
                    <p className="text-xs text-gray-400 text-center mt-1">{role.description}</p>
                    </button>
                );
                })}
            </div>
            {/* AI Response Box */}
            <div className="w-full">
              {loading && (
                <div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
                  <Loader2 className="animate-spin text-blue-400 w-10 h-10 mb-4" />
                  <span className="text-gray-400">Generating AI analysis...</span>
                </div>
              )}
              {!loading && error && (
                <div className="ai-response text-center text-red-400 py-12">{error}</div>
              )}
              {!loading && !error && aiResponse && (
                <div className="ai-response bg-base-100 rounded-xl shadow-xl p-6 animate-fadeIn">
                  <h4 className="font-bold text-lg mb-2">{aiResponse.title}</h4>
                  <p className="mb-4 text-black-400" style={{whiteSpace: 'pre-line'}}>{aiResponse.output}</p>
                  <p className="mb-2 text-gray-400">Tokens used: {aiResponse.tokensUsed}. Cost: ${aiResponse.costUsd}.</p>
                  {cached && (
                    <p className="mb-2 text-gray-400">Viewing cached Interpretation. Last updated: {aiResponse.lastUpdated}.</p>
                  )}
                  <button
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
                    onClick={() => handleGenerate(selectedRole)}
                    disabled={loading || !scanId}
                  >
                    Regenerate Interpretation
                  </button>
                </div>
              )}
              {!loading && !error && !aiResponse && (
                <div className="ai-response text-center text-gray-400 py-12">
                  Select a persona to generate an AI-powered compliance analysis tailored to their perspective.
                </div>
              )}
            </div>
        </div>
    </div>
  );
};

export default InterpreterTab;
